import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Palette,
  Bell,
  Save,
  RefreshCw,
  Brain,
  Upload,
  FileUp,
  Users as UsersIcon,
  ChevronDown,
  Check,
  ArrowRight
} from 'lucide-react';
import Page from '../../components/Page';
import { InfoCard, AlertCard } from '../../components/cards';
import ToggleSwitch from '../../components/ToggleSwitch';
import StatusModal from '../../components/StatusModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useOwner } from '../../contexts/OwnerContext';
import { useModuleConfig } from '../../contexts/ModuleConfigContext';
import { ConfigurationData } from '../../services/api';
import { CSS_CLASSES, CSS_STYLES, ANIMATIONS } from '../../lib/styles';
import ConfigurationSettings from './configuration-settings';

interface Company {
  oid: number;
  name: string;
  documentNumber: string;
  tradeName?: string;
  email?: string;
  phone?: string;
}

const SettingsPage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { isOwnerValid, isLoading: ownerLoading, headers } = useOwner();
  const { refetch: refetchModules } = useModuleConfig();

  // Estados de empresas ativas
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; maxHeight: number; positionAbove: boolean }>({
    top: 0,
    maxHeight: 300,
    positionAbove: false
  });
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Estados locais simples
  const [configData, setConfigData] = useState<ConfigurationData | null>(null);
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loadingConfiguration, setLoadingConfiguration] = useState(false);
  const [configurationError, setConfigurationError] = useState<string | null>(null);
  // Módulos locais da empresa selecionada (não usa contexto para evitar sincronização automática)
  const [localModules, setLocalModules] = useState({
    cte: true,
    nfe: true,
    cd: true,
    logistics: true,
    costs: true,
    clients: true
  });
  // Configurações de aparência locais
  const [localAppearanceSettings, setLocalAppearanceSettings] = useState({
    theme: 'light' as 'light' | 'dark',
    language: 'pt-BR',
    compact_mode: false
  });
  // Estado da modal de status
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    status: 'success' as 'success' | 'error',
    title: '',
    message: ''
  });

  // Carregar empresas ativas ao montar o componente
  useEffect(() => {
    const fetchCompanies = async () => {
      // Aguardar headers estarem carregados
      if (!isOwnerValid || !headers || !headers.userToken || ownerLoading) {
        return;
      }

      setLoadingCompanies(true);
      setCompaniesError(null);

      try {
        const authToken = headers.userToken.startsWith('Bearer ')
          ? headers.userToken
          : `Bearer ${headers.userToken}`;

        const response = await fetch('/api/v1/companies/active', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
            'company': headers.company,
            'businessUnit': headers.businessUnit || '-1',
            'user': headers.user || ''
          },
          body: JSON.stringify({
            index: 0,
            max_records: 100,
            filters: []
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.companies) {
            setCompanies(data.data.companies);
          } else {
            setCompaniesError('Nenhuma empresa ativa encontrada');
          }
        } else if (response.status === 401) {
          setCompaniesError('Erro de autenticação. Headers inválidos.');
          console.error('401 Unauthorized - Headers:', {
            company: headers.company,
            user: headers.user,
            hasToken: !!headers.userToken
          });
        } else {
          setCompaniesError(`Erro ao buscar empresas ativas (${response.status})`);
        }
      } catch (error) {
        setCompaniesError('Erro ao conectar com o servidor');
        console.error('Erro ao buscar empresas:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnerValid, ownerLoading, headers?.company, headers?.userToken]);

  // Buscar configuração da empresa selecionada com headers customizados
  useEffect(() => {
    const fetchConfiguration = async () => {
      // Validações necessárias
      if (!selectedCompany || !isOwnerValid || !headers || !headers.userToken || ownerLoading) {
        return;
      }

      setLoadingConfiguration(true);
      setConfigurationError(null);

      try {
        const authToken = headers.userToken.startsWith('Bearer ')
          ? headers.userToken
          : `Bearer ${headers.userToken}`;

        // Usar o documentNumber da empresa selecionada, sem formatação
        const selectedCompanyWithoutFormatting = selectedCompany.documentNumber.replace(/\D/g, '');

        const response = await fetch('/api/v1/configuration/settings', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
            'company': selectedCompanyWithoutFormatting, // ← CUSTOMIZADO: CNPJ da empresa selecionada
            'businessUnit': headers.businessUnit || '-1',
            'user': headers.user || ''
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setConfigData(data.data);

            // Carregar módulos localmente se existirem na resposta
            if (data.data.moduleSettings) {
              setLocalModules(data.data.moduleSettings);
            } else if (data.data.module_settings) {
              setLocalModules(data.data.module_settings);
            }

            // Carregar aparência localmente se existir na resposta
            // NOTA: NÃO aplicamos o tema aqui - apenas carregamos o valor
            // O tema será aplicado apenas quando a empresa subsidiary entrar no sistema
            if (data.data.appearanceSettings) {
              setLocalAppearanceSettings(data.data.appearanceSettings);
            } else if (data.data.appearance_settings) {
              setLocalAppearanceSettings(data.data.appearance_settings);
            }
          } else {
            setConfigurationError('Falha ao carregar configurações da empresa');
            console.error('Erro na resposta:', data);
          }
        } else if (response.status === 401) {
          setConfigurationError('Erro de autenticação ao buscar configurações');
          console.error('401 Unauthorized - Headers:', {
            company: selectedCompanyWithoutFormatting,
            user: headers.user,
            hasToken: !!headers.userToken
          });
        } else {
          setConfigurationError(`Erro ao buscar configurações (${response.status})`);
        }
      } catch (error) {
        setConfigurationError('Erro ao conectar com o servidor');
        console.error('Erro ao buscar configurações:', error);
      } finally {
        setLoadingConfiguration(false);
      }
    };

    fetchConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, isOwnerValid, ownerLoading, headers?.userToken]);

  // Calcular posição e altura do dropdown
  useEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownDefaultHeight = 300;
      const minHeightToShowBelow = 200; // Espaço mínimo aceitável para rodapé

      // Verificar se há espaço suficiente abaixo
      if (spaceBelow < minHeightToShowBelow && spaceAbove > minHeightToShowBelow) {
        // Posicionar acima
        const maxHeightAbove = Math.min(spaceAbove - 20, 300); // -20 para margem
        setDropdownPosition({
          top: buttonRect.top - maxHeightAbove - 8, // 8px de gap
          maxHeight: maxHeightAbove,
          positionAbove: true
        });
      } else {
        // Posicionar abaixo (padrão)
        const maxHeightBelow = Math.min(spaceBelow - 80, 300); // -80 para deixar margem do rodapé
        setDropdownPosition({
          top: buttonRect.bottom + 8, // 8px de gap
          maxHeight: maxHeightBelow,
          positionAbove: false
        });
      }
    }
  }, [dropdownOpen]);

  // Função para remover formatação do CNPJ (deixar apenas números)
  const removeFormatting = (value: string): string => {
    return value.replace(/\D/g, '');
  };

  // Função de salvar com headers customizados para empresa selecionada
  const handleSave = async () => {
    if (!configData) return;
    if (!selectedCompany) {
      setCompaniesError('Selecione uma empresa antes de salvar');
      return;
    }
    if (!headers || !headers.userToken) {
      setCompaniesError('Headers de autenticação inválidos');
      return;
    }

    setIsSaving(true);

    try {
      // Montar dados completos para salvar
      const dataToSave = {
        ...configData,
        module_settings: localModules,
        appearance_settings: localAppearanceSettings
      };

      // Preparar token de autorização
      const authToken = headers.userToken.startsWith('Bearer ')
        ? headers.userToken
        : `Bearer ${headers.userToken}`;

      // Usar o documentNumber da empresa selecionada, sem formatação
      const selectedCompanyWithoutFormatting = removeFormatting(selectedCompany.documentNumber);

      // Fazer requisição manual com headers customizados
      const response = await fetch('/api/v1/configuration/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
          'company': selectedCompanyWithoutFormatting,
          'businessUnit': headers.businessUnit || '-1',
          'user': headers.user || ''
        },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Recarregar módulos no contexto global para atualizar os menus
          refetchModules();

          setStatusModal({
            isOpen: true,
            status: 'success',
            title: '✨ Configurações Salvas!',
            message: `As configurações de ${selectedCompany.name} foram salvas com sucesso.`
          });
        } else {
          setStatusModal({
            isOpen: true,
            status: 'error',
            title: '❌ Erro ao Salvar',
            message: result.message || 'Ocorreu um erro ao salvar as configurações.'
          });
        }
      } else {
        setStatusModal({
          isOpen: true,
          status: 'error',
          title: '❌ Erro na Requisição',
          message: `Erro HTTP ${response.status}. Tente novamente mais tarde.`
        });
        console.error(`Erro ao salvar - Status: ${response.status}`);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setStatusModal({
        isOpen: true,
        status: 'error',
        title: '❌ Erro de Conexão',
        message: 'Não foi possível conectar ao servidor. Verifique sua conexão.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Atualizar campo simples
  const updateField = (path: string, value: any) => {
    setConfigData(prev => {
      if (!prev) return prev;
      
      const newData = { ...prev } as any;
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newData;
    });
  };

  const handleReset = async () => {
    if (!selectedCompany) {
      setCompaniesError('Selecione uma empresa antes de restaurar');
      return;
    }
    if (!headers || !headers.userToken) {
      setCompaniesError('Headers de autenticação inválidos');
      return;
    }

    try {
      // Resetar para configurações padrão
      const defaultModules = {
        cte: true,
        nfe: true,
        cd: true,
        logistics: true,
        costs: true,
        clients: true
      };

      const defaultNotifications = {
        email: true,
        push: false,
        sms: false
      };

      // Defaults para aparência
      const defaultAppearance = {
        theme: 'light' as 'light' | 'dark',
        language: 'pt-BR',
        compact_mode: false
      };

      // Atualizar estado local
      setLocalModules(defaultModules);
      setLocalAppearanceSettings(defaultAppearance);
      setNotifications(defaultNotifications);

      // NOTA: NÃO alteramos o tema da master
      // Apenas salvamos o padrão no banco para a empresa selecionada

      // Aplicar defaults no configData
      if (configData) {
        const resetData = {
          ...configData,
          ai_query_limit: 1000000,
          business_settings: {
            sacos_por_palete: 0,
            peso_por_saco: 0,
            km_max_route: 0,
            media_sacos_por_order: 0
          },
          job_settings: {
            usa_processamento_customizado_calculo_frete: false,
            vehicle_type_for_job: "",
            calc_freight_veiculo_job: ""
          },
          module_settings: defaultModules,
          appearance_settings: defaultAppearance
        };
        setConfigData(resetData);

        // Preparar token de autorização
        const authToken = headers.userToken.startsWith('Bearer ')
          ? headers.userToken
          : `Bearer ${headers.userToken}`;

        // Usar o documentNumber da empresa selecionada, sem formatação
        const selectedCompanyWithoutFormatting = removeFormatting(selectedCompany.documentNumber);

        const response = await fetch('/api/v1/configuration/settings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
            'company': selectedCompanyWithoutFormatting,
            'businessUnit': headers.businessUnit || '-1',
            'user': headers.user || ''
          },
          body: JSON.stringify(resetData)
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Recarregar módulos no contexto global para atualizar os menus
            refetchModules();

            setStatusModal({
              isOpen: true,
              status: 'success',
              title: '🔄 Configurações Restauradas!',
              message: `As configurações de ${selectedCompany.name} foram restauradas aos valores padrão.`
            });
          } else {
            setStatusModal({
              isOpen: true,
              status: 'error',
              title: '❌ Erro ao Restaurar',
              message: result.message || 'Ocorreu um erro ao restaurar as configurações.'
            });
          }
        } else {
          setStatusModal({
            isOpen: true,
            status: 'error',
            title: '❌ Erro na Requisição',
            message: `Erro HTTP ${response.status}. Tente novamente mais tarde.`
          });
        }
      }

    } catch (error) {
      console.error('Erro ao restaurar:', error);
      setStatusModal({
        isOpen: true,
        status: 'error',
        title: '❌ Erro de Conexão',
        message: 'Não foi possível conectar ao servidor. Verifique sua conexão.'
      });
    }
  };

  // Renderizar página de seleção de empresa enquanto não houver seleção
  if (!selectedCompany) {
    return (
      <Page
        title="⚙️ Configurações"
        subtitle="Selecione uma empresa para configurar"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Select de Empresas Ativas */}
          <div className="w-full max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <InfoCard
                title="Selecione uma Empresa"
                subtitle="Escolha a empresa para a qual deseja configurar"
                icon={UsersIcon}
              >
                <div className="space-y-4">
                  {loadingCompanies ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3">Carregando empresas ativas...</span>
                    </div>
                  ) : companiesError ? (
                    <AlertCard
                      type="error"
                      title="Erro ao carregar empresas"
                      message={companiesError}
                    />
                  ) : companies.length === 0 ? (
                    <AlertCard
                      type="info"
                      title="Nenhuma empresa encontrada"
                      message="Não foram encontradas empresas ativas para configurar."
                    />
                  ) : (
                    <>
                      <div className="w-full">
                        {/* Botão do Dropdown */}
                        <motion.button
                          ref={buttonRef}
                          onClick={() => setDropdownOpen(!dropdownOpen)}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          className="w-full px-4 py-3 rounded-lg flex items-center justify-between transition-all duration-200 relative z-20"
                          style={{
                            backgroundColor: dropdownOpen ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                            borderColor: dropdownOpen ? 'var(--primary)' : 'var(--border-primary)',
                            borderWidth: '2px',
                            color: 'var(--text-primary)'
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <UsersIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                            <span className="font-medium truncate">
                              {selectedCompany?.name || 'Selecione uma empresa...'}
                            </span>
                          </div>
                          <motion.div
                            animate={{ rotate: dropdownOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex-shrink-0 ml-2"
                          >
                            <ChevronDown
                              className="h-5 w-5"
                              style={{ color: 'var(--text-secondary)' }}
                            />
                          </motion.div>
                        </motion.button>
                      </div>

                      {/* Dropdown Menu - Fixed Position para sair do card */}
                      {dropdownOpen && buttonRef.current && (
                        <>
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="fixed rounded-lg shadow-xl z-50"
                            style={{
                              backgroundColor: 'var(--bg-secondary)',
                              border: '1px solid var(--border-primary)',
                              maxHeight: `${dropdownPosition.maxHeight}px`,
                              overflowY: 'auto',
                              top: `${dropdownPosition.top}px`,
                              left: `${buttonRef.current.getBoundingClientRect().left}px`,
                              width: `${buttonRef.current.getBoundingClientRect().width}px`
                            }}
                          >
                            <div>
                              {companies.map((company, index) => (
                                <motion.button
                                  key={company.documentNumber}
                                  onClick={() => {
                                    setSelectedCompany(company);
                                    setDropdownOpen(false);
                                  }}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.02 }}
                                  whileHover={{ backgroundColor: 'var(--bg-tertiary)' }}
                                  className="w-full px-4 py-3 text-left flex items-center justify-between transition-colors hover:bg-opacity-100 first:rounded-t-lg last:rounded-b-lg"
                                  style={{
                                    color: 'var(--text-primary)',
                                    borderBottom: index !== companies.length - 1 ? '1px solid var(--border-primary)' : 'none'
                                  }}
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div
                                      className="h-2 w-2 rounded-full flex-shrink-0"
                                      style={{
                                        backgroundColor: selectedCompany?.documentNumber === company.documentNumber
                                          ? 'var(--primary)'
                                          : 'transparent'
                                      }}
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="font-medium truncate">{company.name}</div>
                                      <div
                                        className="text-xs truncate"
                                        style={{ color: 'var(--text-secondary)' }}
                                      >
                                        {company.documentNumber}
                                      </div>
                                    </div>
                                  </div>
                                  {selectedCompany?.documentNumber === company.documentNumber && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      transition={{ type: 'spring', stiffness: 300 }}
                                      className="flex-shrink-0 ml-2"
                                    >
                                      <Check
                                        className="h-5 w-5"
                                        style={{ color: 'var(--primary)' }}
                                      />
                                    </motion.div>
                                  )}
                                </motion.button>
                              ))}
                            </div>
                          </motion.div>

                          {/* Overlay para fechar ao clicar fora */}
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setDropdownOpen(false)}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </InfoCard>
            </motion.div>
          </div>
        </motion.div>
      </Page>
    );
  }

  // Carregar configuração da empresa selecionada
  if (loadingConfiguration || !configData) {
    return (
      <Page
        title="⚙️ Configurações"
        subtitle={`Configurando: ${selectedCompany?.name}`}
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Carregando configurações...</span>
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="⚙️ Configurações"
      subtitle={`Configurando: ${selectedCompany?.name}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Botão para mudar de empresa */}
        <div className="flex justify-start mb-6">
          <motion.button
            onClick={() => setSelectedCompany(null)}
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
            className="px-5 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
            style={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none'
            }}
          >
            <span>Trocar de Empresa</span>
            <motion.div
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <ArrowRight className="h-5 w-5" />
            </motion.div>
          </motion.button>
        </div>
        {/* Module Configuration Card */}
        <InfoCard
          title="Configuração de Módulos"
          subtitle="Escolha quais módulos deseja que o sistema analise e exiba no menu"
          icon={SettingsIcon}
          animation={{
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            transition: { delay: 0.4 }
          }}
        >
          <div className={CSS_CLASSES.SPACE_Y_6}>
            <div className={CSS_CLASSES.GRID_RESPONSIVE}>
              {[
                { 
                  key: 'cte', 
                  label: 'CTEs', 
                  description: 'Análise de Conhecimentos de Transporte Eletrônico',
                  icon: '📋'
                },
                { 
                  key: 'nfe', 
                  label: 'NFes', 
                  description: 'Análise de Notas Fiscais Eletrônicas',
                  icon: '🧾'
                },
                { 
                  key: 'cd', 
                  label: 'Centro de Distribuição', 
                  description: 'Análise de Centros de Distribuição',
                  icon: '🏢'
                },
                { 
                  key: 'logistics', 
                  label: 'Malha Logística', 
                  description: 'Análise da rede logística e força de venda',
                  icon: '🗺️'
                },
                { 
                  key: 'costs', 
                  label: 'Custos', 
                  description: 'Análise de custos operacionais',
                  icon: '💰'
                },
                { 
                  key: 'clients', 
                  label: 'Clientes', 
                  description: 'Análise de dados de clientes',
                  icon: '👥'
                }
              ].map((module) => (
                <motion.div
                  key={module.key}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + Math.random() * 0.2 }}
                  className={`${CSS_CLASSES.CARD_BASE} ${CSS_CLASSES.CARD_CLICKABLE}`}
                  style={{
                    backgroundColor: localModules[module.key as keyof typeof localModules]
                      ? 'var(--bg-secondary)'
                      : 'var(--bg-tertiary)',
                    borderColor: localModules[module.key as keyof typeof localModules]
                      ? 'var(--primary)'
                      : 'var(--border-primary)'
                  }}
                >
                  <div className={CSS_CLASSES.FLEX_BETWEEN}>
                    <div className={`${CSS_CLASSES.FLEX_START} flex-1`}>
                      <div className="text-2xl">{module.icon}</div>
                      <div className="flex-1">
                        <h3
                          className={CSS_CLASSES.TEXT_PRIMARY}
                          style={CSS_STYLES.TEXT_PRIMARY}
                        >
                          {module.label}
                        </h3>
                        <p
                          className={CSS_CLASSES.TEXT_SECONDARY}
                          style={CSS_STYLES.TEXT_SECONDARY}
                        >
                          {module.description}
                        </p>
                      </div>
                    </div>
                           <div className={CSS_CLASSES.FLEX_CENTER}>
                             <ToggleSwitch
                               checked={localModules[module.key as keyof typeof localModules]}
                               onChange={(checked) => {
                                 const newModules = {
                                   ...localModules,
                                   [module.key]: checked
                                 };
                                 setLocalModules(newModules);
                               }}
                               size="md"
                               color="blue"
                             />
                           </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <AlertCard
              type="info"
              title={`Módulos Ativos: ${Object.values(localModules).filter(Boolean).length} de ${Object.keys(localModules).length}`}
              message="Os módulos desabilitados não aparecerão no menu principal e não terão suas análises executadas pelo sistema."
              animation={ANIMATIONS.FADE_IN_DELAYED}
            />
          </div>
        </InfoCard>

        {/* Main Settings Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Appearance Settings */}
          <InfoCard
            title="Aparência"
            subtitle="Personalize a interface"
            icon={Palette}
            animation={{
              initial: { opacity: 0, x: -20 },
              animate: { opacity: 1, x: 0 },
              transition: { delay: 0.4 }
            }}
          >
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
                  Tema
                </label>
                <div className="space-y-3">
                  {[
                    { value: 'light', label: 'Claro', description: 'Tema claro para uso diurno' },
                    { value: 'dark', label: 'Escuro', description: 'Tema escuro para uso noturno' }
                  ].map((themeOption) => (
                    <label key={themeOption.value} className="flex items-center p-3 rounded-lg cursor-pointer" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <input
                        type="radio"
                        name="theme"
                        value={themeOption.value}
                        checked={localAppearanceSettings.theme === themeOption.value}
                        onChange={() => {
                          // Apenas atualizar o valor local
                          // NÃO aplicar o tema imediatamente - será salvo no banco
                          // O tema será aplicado quando a empresa entrar no sistema
                          setLocalAppearanceSettings({
                            ...localAppearanceSettings,
                            theme: themeOption.value as 'light' | 'dark'
                          });
                        }}
                        className="mr-3"
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      <div>
                        <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {themeOption.label}
                        </div>
                        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {themeOption.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Notifications Settings */}
          <InfoCard
            title="Notificações"
            subtitle="Configure alertas e notificações"
            icon={Bell}
            animation={{
              initial: { opacity: 0, x: 20 },
              animate: { opacity: 1, x: 0 },
              transition: { delay: 0.5 }
            }}
          >
            <div className="space-y-4">
              {[
                { key: 'email', label: 'Notificações por Email', description: 'Receba alertas importantes por email' },
                { key: 'push', label: 'Notificações Push', description: 'Alertas em tempo real no navegador' },
                { key: 'sms', label: 'Notificações SMS', description: 'Mensagens de texto para alertas críticos' }
              ].map((notification) => (
                <div key={notification.key} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <div>
                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {notification.label}
                    </div>
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {notification.description}
                    </div>
                  </div>
                  <ToggleSwitch
                    checked={notifications[notification.key as keyof typeof notifications]}
                    onChange={(checked) => setNotifications(prev => ({
                      ...prev,
                      [notification.key]: checked
                    }))}
                    size="md"
                    color="blue"
                  />
                </div>
              ))}
            </div>
          </InfoCard>
        </div>

        {/* IA e Processamento Configuration */}
        <InfoCard
          title="Configurações de IA e Processamento"
          subtitle="Configure os parâmetros de análise de IA e processamento de dados"
          icon={Brain}
          animation={{
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            transition: { delay: 0.6 }
          }}
        >
          <ConfigurationSettings configData={configData} updateField={updateField} />
        </InfoCard>

        {/* Data Upload Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Send Data for Analysis Card */}
          <Link to="/logistics/send-data-for-analysis">
            <motion.div
              whileHover={{ scale: 1.02, shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
              className={`${CSS_CLASSES.CARD_BASE} ${CSS_CLASSES.CARD_CLICKABLE} h-full cursor-pointer`}
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-primary)',
                transition: 'all 0.3s ease'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="flex items-center space-x-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <FileUp className="h-8 w-8" style={{ color: '#3b82f6' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    📊 Enviar Análise de Dados
                  </h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Envie sua planilha de análise de dados para processamento com IA
                  </p>
                </div>
                <Upload className="h-5 w-5" style={{ color: 'var(--text-tertiary)' }} />
              </div>
            </motion.div>
          </Link>

          {/* Send Clients Portfolio Card */}
          <Link to="/clients/send-clients-portfolio">
            <motion.div
              whileHover={{ scale: 1.02, shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
              className={`${CSS_CLASSES.CARD_BASE} ${CSS_CLASSES.CARD_CLICKABLE} h-full cursor-pointer`}
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-primary)',
                transition: 'all 0.3s ease'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center space-x-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                  <UsersIcon className="h-8 w-8" style={{ color: '#22c55e' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    👥 Enviar Carteira de Clientes
                  </h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Importe sua carteira de clientes para gerenciamento e análise
                  </p>
                </div>
                <Upload className="h-5 w-5" style={{ color: 'var(--text-tertiary)' }} />
              </div>
            </motion.div>
          </Link>
        </div>

        {/* Action Buttons */}
        <div className={CSS_CLASSES.FLEX_CENTER}>
          <div className="flex space-x-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`${CSS_CLASSES.BUTTON_PRIMARY} flex items-center space-x-2 hover:bg-blue-700 disabled:hover:bg-blue-500`}
              style={{ 
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                opacity: isSaving ? 0.7 : 1,
                border: 'none'
              }}
            >
              {isSaving ? (
                <RefreshCw className={CSS_CLASSES.ICON_MEDIUM + ' animate-spin'} />
              ) : (
                <Save className={CSS_CLASSES.ICON_MEDIUM} />
              )}
              <span>
                {isSaving ? 'Salvando...' : 'Salvar Configurações'}
              </span>
            </button>

            <button
              onClick={handleReset}
              className={`${CSS_CLASSES.BUTTON_PRIMARY} flex items-center space-x-2`}
              style={CSS_STYLES.BUTTON_SECONDARY}
            >
              <RefreshCw className={CSS_CLASSES.ICON_MEDIUM} />
              <span>Restaurar Padrões</span>
            </button>
          </div>
        </div>

      </motion.div>

      {/* Status Modal - Elegante com overlay */}
      <StatusModal
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal({ ...statusModal, isOpen: false })}
        status={statusModal.status}
        title={statusModal.title}
        message={statusModal.message}
        autoClose={4000}
      />
    </Page>
  );
};

export default SettingsPage;
